/**
 * ICICI UPI Webhook Schemas
 *
 * Validates incoming webhook notifications from ICICI Bank's UPI payment gateway.
 */

import { z } from 'zod';

// Transaction status enum based on ICICI UPI webhook specification
export const ICICIUPITxnStatusEnum = z.enum(['SUCCESS', 'FAILED', 'PENDING']);
export type ICICIUPITxnStatus = z.infer<typeof ICICIUPITxnStatusEnum>;

// Date format: YYYYMMDDHHmmss (e.g., "20160715142352")
const iciciDateFormat = z
  .string()
  .regex(/^\d{14}$/, 'Date must be in format YYYYMMDDHHmmss');

/**
 * ICICI UPI Webhook Payload Schema
 *
 * Contains payer details: PayerName, PayerMobile, PayerVA (UPI VPA)
 */
export const ICICIUPIWebhookPayloadSchema = z.object({
  merchantId: z.string().min(1, 'merchantId is required'),
  subMerchantId: z.string().optional(),
  terminalId: z.string().min(1, 'terminalId is required'),
  BankRRN: z.string().min(1, 'BankRRN (Bank Reference Number) is required'),
  merchantTranId: z.string().min(1, 'merchantTranId is required'),
  PayerName: z.string().min(1, 'PayerName is required'),
  PayerMobile: z.string().regex(/^\d{10}$/, 'PayerMobile must be 10 digits'),
  PayerVA: z.string().min(1, 'PayerVA (Virtual Address) is required'),
  PayerAmount: z.string().min(1, 'PayerAmount is required'),
  TxnStatus: ICICIUPITxnStatusEnum,
  TxnInitDate: iciciDateFormat,
  TxnCompletionDate: iciciDateFormat,
});
export type ICICIUPIWebhookPayload = z.infer<typeof ICICIUPIWebhookPayloadSchema>;

/**
 * Parse ICICI date format (YYYYMMDDHHmmss) to JavaScript Date
 */
export function parseICICIDate(dateStr: string): Date {
  const year = parseInt(dateStr.substring(0, 4), 10);
  const month = parseInt(dateStr.substring(4, 6), 10) - 1;
  const day = parseInt(dateStr.substring(6, 8), 10);
  const hour = parseInt(dateStr.substring(8, 10), 10);
  const minute = parseInt(dateStr.substring(10, 12), 10);
  const second = parseInt(dateStr.substring(12, 14), 10);
  return new Date(year, month, day, hour, minute, second);
}

/**
 * Map ICICI transaction status to internal payment status
 */
export function mapICICIStatusToPaymentStatus(
  iciciStatus: ICICIUPITxnStatus
): 'COMPLETED' | 'FAILED' | 'PENDING' {
  switch (iciciStatus) {
    case 'SUCCESS':
      return 'COMPLETED';
    case 'FAILED':
      return 'FAILED';
    case 'PENDING':
    default:
      return 'PENDING';
  }
}
